import { getBasketIdBasedRateLimitingHeader } from './apiHeaders';
import { config } from './config';
import createApiError, { ApiError } from './createApiError';
import { getDevice } from './useIsMobileDevice';

type QueuedRequest<T = any> = {
  options: ApiCallOptions;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
};

const queuedRequests: QueuedRequest[] = [];

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiCallOptions = {
  method?: Method;
  endpoint: string;
  payload?: any;
  urlParams?: Record<string, string>;
  flow?: 'state' | 'legacy';
  allowedStatusCodes?: number[];
  failedStatusCodes?: number[];
  sequential?: boolean;
  jwt?: string;
};

const API_URL = '/api/co/v3';

const LEGACY_API_URL = '/api';

const callApi = async <T = any>({ sequential, ...options }: ApiCallOptions): Promise<T> => {
  if (sequential) {
    return queueRequest(options);
  }
  const { method, endpoint, payload, urlParams, flow, allowedStatusCodes, failedStatusCodes, jwt } = options;
  const headers: Record<string, string> = {
    'X-Shop-ID': config.appId.toString(),
    'X-DEVICE': getDevice()
  };
  if (config.session.token) {
    headers['X-Session'] = config.session.token;
  }
  if (config.useCheckoutNext) {
    headers['X-USE-CHECKOUT-NEXT'] = 'true';
  }
  if (config.session.accessToken) {
    headers.Authorization = `Bearer ${config.session.accessToken}`;
  }
  if (config.session.correlationId) {
    headers['X-Correlation-ID'] = config.session.correlationId;
  }
  const basketIdBasedRateLimitingHeader = getBasketIdBasedRateLimitingHeader(jwt ?? payload?.jwt);
  if (basketIdBasedRateLimitingHeader) {
    headers['X-R-Basket-ID'] = basketIdBasedRateLimitingHeader;
  }

  // CT-280864 -> use legacyApiHost for auth based endpoints
  const legacyEndpoints = [
    '/customer', //                     registration - guest
    '/customer/login', //               login
    '/customer/password', //            reset password
    '/customer/password/reset', //      request password reset
    '/customer/authenticate', //        set password for customer(witt)
    '/customer/publicKeyBirthday' //    login via customerId and birhtdate(witt)
  ];

  const apiHost = legacyEndpoints.includes(endpoint) ? (config.legacyApiHost ?? config.apiHost) : config.apiHost;

  const url =
    apiHost +
    (flow === 'legacy' ? LEGACY_API_URL : API_URL) +
    endpoint +
    (urlParams ? `?${new URLSearchParams(urlParams).toString()}` : '');
  let fetchOptions: RequestInit = { headers, method, credentials: 'include' };
  if (payload) {
    headers['Content-Type'] = 'application/json';
    fetchOptions = { ...fetchOptions, body: JSON.stringify(payload), method: fetchOptions.method ?? 'POST' };
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 401) {
    window.location.reload();
    throw new ApiError('401 (Unauthorized) received - Reloading page...', response.status);
  } else if (
    (response.status >= 400 && !allowedStatusCodes?.includes(response.status)) ||
    failedStatusCodes?.includes(response.status)
  ) {
    throw await createApiError(response);
  } else if (
    response.status === 204 ||
    (flow === 'legacy' && (method === 'DELETE' || endpoint === '/customer/password/reset'))
  ) {
    return null as unknown as T;
  } else {
    return response.json();
  }
};

const queueRequest = <T = any>(options: ApiCallOptions): Promise<T> => {
  const promise: Promise<T> = new Promise((resolve, reject) => queuedRequests.push({ options, resolve, reject }));

  if (queuedRequests.length === 1) {
    runQueuedRequests();
  }

  return promise;
};

const runQueuedRequests = async () => {
  const requestToRun = queuedRequests[0];
  try {
    const response = await callApi(requestToRun.options);
    requestToRun.resolve(response);
  } catch (error) {
    requestToRun.reject(error);
  }

  queuedRequests.shift();

  if (queuedRequests.length > 0) {
    runQueuedRequests();
  }
};

export default callApi;
